import React from 'react';
import { AlertTriangle } from 'lucide-react';
import { motion } from 'framer-motion';
import { getScoreTone } from '../utils/formatters';
import GradeRing from './GradeRing';

const EASE = [0.22, 1, 0.36, 1];

const CAT_VERDICT = {
  strong: 'Ahead of most local rivals.',
  fair: 'Solid - with room to gain.',
  warning: 'Falling behind competitors.',
  critical: 'Costing you customers now.'
};

const ordinal = (n) => {
  const v = Math.round(Number(n) || 0);
  const m = v % 100;
  const s = ['th', 'st', 'nd', 'rd'];
  return `${v}${s[(m - 20) % 10] || s[m] || s[0]}`;
};

const PreviewPhase = ({ preview, previewRevealStage, pillarCards, beginUnlockFlow }) => {
  const tone = getScoreTone(preview.overall);
  const pos = preview.rank?.position;
  const total = preview.rank?.total;
  const upside = Number(preview.annualUpside || 0);

  return (
    <motion.div
      className="vlg-phase"
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -6 }}
      transition={{ duration: 0.36, ease: EASE }}
    >
      {preview.isDemo && (
        <div className="vlg-demo-badge" role="note">Sample result — illustrative demo, not a live scan</div>
      )}

      {/* Dominant grade hero */}
      <div className="vlg-grade">
        <GradeRing score={preview.overall} letter={preview.letterGrade} tone={tone} />
        <div className="vlg-grade__body">
          <p className="vlg-grade__eyebrow">Your competition-adjusted grade</p>
          <p className="vlg-grade__summary">
            {pos && total
              ? <>You rank <b>#{pos} of {total}</b> local competitors.</>
              : <>Here&apos;s how your visibility compares locally.</>}
          </p>
          <p className="vlg-grade__rankline">
            {ordinal(preview.percentile || 0)} percentile{preview.tier ? ` · ${preview.tier}` : ''}
            {preview.limitedCompetitionData ? ' · provisional cohort' : ''}
          </p>
        </div>
      </div>

      {/* Category grid */}
      <div className={`vlg-block reveal-block ${previewRevealStage >= 1 ? 'is-visible' : ''}`}>
        <p className="vlg-block__title" role="heading" aria-level="2">How you score</p>
        <div className="vlg-cats">
          {pillarCards.map((pillar) => {
            const t = getScoreTone(pillar.value);
            return (
              <div className="vlg-cat" data-tone={t} key={pillar.key}>
                <div className="vlg-cat__top">
                  <span className="vlg-cat__name">{pillar.label}</span>
                  <span className="vlg-cat__score">{pillar.value}<small>/100</small></span>
                </div>
                <div className="vlg-cat__bar">
                  <div className="vlg-cat__fill" style={{ width: `${Math.max(4, Math.min(100, pillar.value))}%` }} />
                </div>
                <p className="vlg-cat__verdict">{CAT_VERDICT[t]}</p>
              </div>
            );
          })}
        </div>
      </div>

      {/* Top fixes */}
      {(preview.issueExamples || []).length > 0 && (
        <div className={`vlg-block reveal-block ${previewRevealStage >= 2 ? 'is-visible' : ''}`}>
          <p className="vlg-block__title" role="heading" aria-level="2">
            {preview.issueCount ? `${preview.issueCount} issues found - top priorities` : 'Top priorities'}
          </p>
          <ul className="vlg-fixes">
            {(preview.issueExamples || []).map((issue) => (
              <li className="vlg-fix" key={issue.title}>
                <AlertTriangle className="vlg-fix__icon" size={17} aria-hidden="true" />
                <div>
                  <div className="vlg-fix__title">{issue.title}</div>
                  {issue.detail && <p className="vlg-fix__detail">{issue.detail}</p>}
                </div>
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* What the gap is worth */}
      <div className={`vlg-block reveal-block ${previewRevealStage >= 2 ? 'is-visible' : ''}`}>
        <p className="vlg-summaryline">
          <strong>{preview.competitorCount || 0} local competitors</strong> are currently more visible than you.
          {preview.aiInsights?.summary ? ` ${preview.aiInsights.summary}` : ''}
        </p>
        {upside > 0 && (
          <p className="vlg-summaryline">
            Closing these gaps could be worth roughly{' '}
            <span className="vlg-upside">${upside.toLocaleString()}/year</span> — an illustrative estimate, not a guarantee.
          </p>
        )}
      </div>

      {/* Single CTA */}
      <div className={`vlg-cta reveal-block ${previewRevealStage >= 3 ? 'is-visible' : ''}`}>
        <p className="vlg-cta__copy">
          Unlock the full league table, a module-by-module audit, and your prioritized plan
          to <strong>improve your local visibility</strong>.
        </p>
        <button className="vlg-btn" type="button" onClick={beginUnlockFlow}>
          Unlock full competitor report
        </button>
      </div>

      <p className="vlg-disclaimer">
        Scores and figures are estimates based on public data and a general model — informational only, not a guarantee of results.
      </p>
    </motion.div>
  );
};

export default PreviewPhase;
